import classNames from 'classnames';
import { cloneElement, isValidElement, ReactNode } from 'react';
import { Link } from 'wouter';

import PillButton from '../buttons/PillButton';
import Image from '../partials/Image';
import Sponsor from '../partials/Sponsor';
import SummaryDescription from '../typography/SummaryDescription';
import SummaryTitle from '../typography/SummaryTitle';
import SurtitleTypography from '../typography/Surtitle';

import styles from '../../styles/summaries/summary.module.css';

export interface SummaryProps {
    title?: string | null;
    surtitle?: string | null;
    description?: string | null;
    sponsor?: Sponsor | null;
    image?: Image | ReactNode;
    imagePosition?: 'top' | 'inline' | null;
    icon?: ReactNode;
    buttonLabel?: ReactNode;
    link?: string | null;
    children?: ReactNode;
    placeholder?: boolean;
    withLink?: boolean;
    withButton?: boolean;
    withoutImage?: boolean;
    withoutTitle?: boolean;
    withoutDescription?: boolean;
    withoutSurtitle?: boolean;
    withoutSponsor?: boolean;
    className?: string | null;
    innerClassName?: string | null;
    iconClassName?: string | null;
    imageClassName?: string | null;
    pictureClassName?: string | null;
    titleClassName?: string | null;
    surtitleClassName?: string | null;
    descriptionClassName?: string | null;
    buttonClassName?: string | null;
}

function Summary({
    surtitle = null,
    title = null,
    description = null,
    sponsor = null,
    image = null,
    imagePosition = null,
    icon = null,
    buttonLabel = 'Voir',
    link = null,
    children = null,
    placeholder = false,
    withLink = false,
    withButton = false,
    withoutImage = false,
    withoutTitle = false,
    withoutDescription = false,
    withoutSurtitle = false,
    withoutSponsor = false,
    className = null,
    innerClassName = null,
    iconClassName = null,
    imageClassName = null,
    pictureClassName = null,
    titleClassName = null,
    surtitleClassName = null,
    descriptionClassName = null,
    buttonClassName = null,
}: SummaryProps) {
    const inner = (
        <div className={classNames([styles.inner, innerClassName])}>
            {icon !== null ? (
                <div className={classNames([styles.icon, iconClassName])}>{icon}</div>
            ) : null}
            {!withoutImage && (image !== null || placeholder) && !isValidElement(image) ? (
                <Image
                    className={classNames([
                        styles.image,
                        {
                            [imageClassName]: imageClassName !== null,
                        },
                    ])}
                    pictureClassName={classNames([styles.picture, pictureClassName])}
                    media={image}
                    size="small"
                    withPlaceholder={placeholder}
                />
            ) : null}
            {!withoutImage && image !== null && isValidElement(image)
                ? cloneElement(image, {
                      className: classNames([
                          styles.image,
                          {
                              [imageClassName]: imageClassName !== null,
                          },
                      ]),
                  })
                : null}
            {!withoutSurtitle && surtitle !== null ? (
                <SurtitleTypography
                    className={classNames([styles.surtitle, surtitleClassName])}
                    placeholder={placeholder}
                >
                    {surtitle}
                </SurtitleTypography>
            ) : null}
            {!withoutTitle ? (
                <SummaryTitle
                    className={classNames([styles.title, titleClassName])}
                    placeholder={placeholder}
                >
                    {title}
                </SummaryTitle>
            ) : null}

            {!withoutDescription && (description !== null || placeholder) ? (
                <SummaryDescription
                    className={classNames([styles.description, descriptionClassName])}
                    html={description}
                    placeholder={placeholder}
                >
                    {null}
                </SummaryDescription>
            ) : null}
            {sponsor !== null && !withoutSponsor ? (
                <Sponsor
                    {...sponsor}
                    withImage
                    className={styles.sponsor}
                    imageClassName={styles.sponsorImage}
                />
            ) : null}
            {children !== null ? <div className={styles.children}>{children}</div> : null}
            {withButton && buttonLabel !== null && link !== null ? (
                <PillButton
                    href={link}
                    compact
                    bordered
                    className={classNames([styles.button, buttonClassName])}
                >
                    {buttonLabel}
                    {/* <FormattedMessage
                defaultMessage="{label}"
                description="Label button"
                values={{
                    label: buttonLabel,
                }}
            /> */}
                </PillButton>
            ) : null}
        </div>
    );

    if (withLink && link !== null) {
        return (
            <Link
                href={link}
                className={classNames([
                    styles.container,
                    {
                        [styles.withIcon]: icon !== null,
                        [styles.withoutImage]: (image === null && !placeholder) || withoutImage,
                        [styles[imagePosition]]:
                            imagePosition !== null &&
                            (image !== null || icon !== null || placeholder),
                    },
                    className,
                ])}
            >
                {inner}
            </Link>
        );
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withIcon]: icon !== null,
                    [styles.withoutImage]: (image === null && !placeholder) || withoutImage,
                    [styles[imagePosition]]:
                        imagePosition !== null && (image !== null || icon !== null || placeholder),
                },
                className,
            ])}
        >
            {inner}
        </div>
    );
}

export default Summary;
